import { useEffect, useState } from 'react';
import type { MetaFields } from '@dutchiesdk/ecommerce-extensions-sdk';
import { useRemoteCommerceComponents } from './remote-theme';
import { useCommerceBridge } from './bridge/bridge-context';

/**
 * Hook to get meta fields from the remote theme's getStoreFrontMetaFields function
 * Returns the MetaFields object that can be used to render meta tags
 */
export const useMetaFields = (skipExtraction = false): MetaFields | null => {
  const [metaFields, setMetaFields] = useState<MetaFields | null>(null);
  const { remoteThemeModule } = useRemoteCommerceComponents();
  const dataBridgeData = useCommerceBridge();
  const isBridgeReady = dataBridgeData != null;

  // Extract stable values to avoid infinite loops
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const getMetaFieldsFn = remoteThemeModule.default?.getStoreFrontMetaFields;

  useEffect(() => {
    let isCurrentRequest = true;

    const loadMetaFields = async (): Promise<void> => {
      if (!getMetaFieldsFn || dataBridgeData == null || skipExtraction) {
        setMetaFields(null);
        return;
      }

      try {
        const fields = await getMetaFieldsFn(dataBridgeData);
        if (isCurrentRequest) {
          setMetaFields(fields);
        }
      } catch {
        if (isCurrentRequest) {
          setMetaFields(null);
        }
      }
    };

    void loadMetaFields();
    // The shared bridge is stable, so only readiness (not cart/user value changes) participates in
    // extraction. This also clears metadata if the provider graph becomes unavailable.
    return () => {
      isCurrentRequest = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, getMetaFieldsFn, isBridgeReady, skipExtraction]);

  return metaFields;
};
